import TrackingContext, { TrackingProps } from 'contexts/trackingContext'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { productClick, productImpression } from 'analytics/enhanced-ecommerce'
import { GAImpressionFieldObject, GAProductFieldObject, productImpressionGenericObject } from 'analytics/eventDefinitions'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import CloseButton from 'components/Common/CloseButton'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import Image from 'components/Common/Image'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import Group from 'components/utils/Group'
import Pill from 'components/Luxkit/Pill'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import Clickable from 'components/Common/Clickable/Clickable'
import { useInView } from 'react-intersection-observer'
import { CAROUSEL_CARD_WIDTH } from 'home/components/CarouselCard/CarouselCard'
import { clickHomepageRecommendationTileEvent } from 'analytics/optimizely/events'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'
import config from 'constants/config'

const ImageBlock = styled.div`
  flex-shrink: 0;
  width: ${rem(68)};
  height: ${rem(92)};
`

const Root = styled(Clickable)`
  align-self: start;
  background-color: ${props => props.theme.palette.neutral.eight};
  overflow: hidden;
  transition: outline 0.2s, background-color 0.2s;

  width: 75vw;

  // on tablet we match the offer card size
  ${mediaQueryUp.tablet} {
    width: ${rem(CAROUSEL_CARD_WIDTH)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(8)};
    // the width is unset since the card carousel should auto size it
    width: unset;
    // the min-width is used for new home page where the full width carousel does not control tile width
    min-width: ${rem(320)};

    // on desktop, close button is hidden unless hover
    .high-intent-close-button {
      transition: opacity 0.2s;
      opacity: 0;
      pointer-events: none;
    }

    &:hover {
      background-color: ${props => props.theme.palette.neutral.seven};

      .high-intent-close-button {
        pointer-events: auto;
        opacity: 1;
      }
    }
  }

  &.tile-border {
    border: 1px solid ${props => props.theme.palette.neutral.five};
    border-radius: ${props => props.theme.borderRadius.small};
  }

  .input-keyboard &:focus {
    outline: 2px solid  ${props => props.theme.palette.neutral.five};
    outline-offset: 2px;
  }
`

const ContentGroup = styled(Group)`
  flex-grow: 1;
`

export interface HighIntentTileProps {
  offerId: string;
  highIntentCategory: App.HighIntentCategory;
  url: string;
  title: string;
  image: App.Image;
  checkInOutText?: string;
  occupancyText?: string;
  urgencyLabels?: App.OfferUrgencyLabel[];
  durationText?: string;
  price?: number;
  discountText?: string;
  position: number;
  tracking?: TrackingProps;
  testId: string;
  onClick?: (offerId: string) => void
  onClose?: (offerId: string, category: App.HighIntentCategory) => void
  className?: string;
}

function HighIntentRecommendationTile(props: HighIntentTileProps) {
  const {
    offerId,
    highIntentCategory,
    url,
    title,
    image,
    checkInOutText,
    occupancyText,
    urgencyLabels,
    durationText,
    price,
    discountText,
    position,
    tracking,
    testId,
    onClick,
    onClose,
    className,
  } = props

  const { currentCurrency } = useContext(GeoContext)

  // ------- Render props -------------
  const showPrice = (!!price && price > 0)
  const checkInOutAndOccupancyText = [checkInOutText, occupancyText].filter((str) => str).join(', ')

  // if there is no date/occupancy, we can use 2 lines for the title
  const titleLines = checkInOutAndOccupancyText ? 1 : 2
  // we want to use letter wrap so the single line clamp can break mid-word
  const titleWrap: React.ComponentProps<typeof Caption>['wrap'] = (titleLines === 1) ? 'letter-wrap' : undefined

  // ------- Analytics -------------
  const [inViewRef, inView] = useInView({ triggerOnce: true })

  const trackingCtx = useContext(TrackingContext)
  const trackingProps = useMemo(() => ({
    ...trackingCtx,
    ...tracking, // explicit tracking props override inherited tracking context
  }), [tracking, trackingCtx])
  const hasTracking = !!trackingCtx || !!tracking

  const listId = trackingProps?.listId ?? 'unknown'
  const listName = trackingProps?.listName ?? 'High Intent Recommendations'
  const category = trackingProps?.category ?? 'unknown'
  const lereVersion = trackingProps?.lere?.version ?? 'unknown'

  const onImpression = useCallback(() => {
    const impression: GAImpressionFieldObject = productImpressionGenericObject({
      id: offerId,
      name: title,
      category,
      price,
      position,
      listName,
    })
    productImpression(currentCurrency, [impression], {
      listId,
      listName,
    }, lereVersion)
  }, [listId, offerId, title, category, currentCurrency, price, listName, position, lereVersion])

  useEffect(() => {
    if (inView && hasTracking) {
      onImpression()
    }
  }, [inView, hasTracking, onImpression])

  const fireProductClick = useCallback(() => {
    if (!hasTracking) { return }

    const productClickObject: GAProductFieldObject = {
      id: offerId,
      name: title,
      category,
      price,
      quantity: 1,
      position,
    }
    productClick(currentCurrency, productClickObject, {
      listId,
      listName,
    }, lereVersion)
  }, [
    hasTracking,
    category,
    currentCurrency,
    title,
    offerId,
    position,
    price,
    listId,
    listName,
    lereVersion,
  ])

  const analyticsPage = useContext(AnalyticsPageContext)

  const handleClick = useCallback(() => {
    fireProductClick()
    if (analyticsPage === 'homepage' || analyticsPage === 'homepage_old') {
      Analytics.track('click-homepage-recommendation-tile', clickHomepageRecommendationTileEvent())
    }
    onClick?.(offerId)
  }, [fireProductClick, onClick, offerId, analyticsPage])

  const handleDismissed = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onClose?.(offerId, highIntentCategory)
    if (hasTracking) {
      Analytics.track('client-event', clientEvent(`${listId} ${offerId}`, `${highIntentCategory}_tile_dismissed`, 'lere', 'interaction'))
    }
  }, [onClose, hasTracking, listId, offerId, highIntentCategory])

  const linkParams = useMemo(() => {
    return {
      onClick: handleClick,
      'data-testid': testId,
      to: url,
      ...(config.OPEN_NEW_TAB_OFFER_CLICK ? { target: '_blank' } : {}),
    }
  }, [handleClick, testId, url])

  return (
    <Root {...linkParams} ref={inViewRef} className={className}>
      <Group direction="horizontal" verticalAlign="stretch" fullWidth gap={12}>
        <ImageBlock>
          <Image
            id={image.id}
            alt={image.title}
            width={68}
            height={92}
            dpr={2}
            fit="center"
          />
        </ImageBlock>
        <ContentGroup direction="vertical" gap={4} verticalAlign="space-between">
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" weight="bold" lineClamp={titleLines} wrap={titleWrap}>{title}</BodyText>
            {!!checkInOutAndOccupancyText && !urgencyLabels?.length && <Caption variant="large" lineClamp={1} wrap="letter-wrap">
              {checkInOutAndOccupancyText}
            </Caption>}
            {!!urgencyLabels?.length && <OfferUrgencyLabels urgencyLabels={urgencyLabels} countDownShowHourOnly />}
          </Group>
          {showPrice && <Group direction="vertical" gap={4}>
            {!!durationText && <Caption variant="large">{durationText}</Caption>}
            <Group direction="horizontal" gap={4}>
              <BodyText variant="large" weight="bold">
                <FormatCurrency value={price} format="roundedDollar" />
              </BodyText>
              {!!discountText && <Pill kind="primary" variant="success">
                {discountText}
              </Pill>}
            </Group>
          </Group>}
        </ContentGroup>
        {onClose && <CloseButton
          className="high-intent-close-button"
          onClick={handleDismissed}
        />}
      </Group>
    </Root>
  )
}

export default HighIntentRecommendationTile
